export interface ApiResponse<T = unknown> {
  data: T | null;
  error: string | null;
}

const handleResponse = async <T>(response: Response): Promise<ApiResponse<T>> => {
  // Check status first — server error pages and redirects may return non-JSON
  // bodies, so attempting response.json() on those would yield a misleading
  // "Invalid JSON response" rather than the real cause.
  if (response.status >= 500) {
    throw new Error('There was a server error. Please contact support.');
  }

  if (response.status === 302) {
    throw new Error('Login expired. Please refresh the page.');
  }

  let result: { data?: T; error?: string };
  try {
    result = await response.json();
  } catch {
    throw new Error('Invalid JSON response from server.');
  }

  if (!response.ok) {
    throw new Error(result.error || `HTTP error ${response.status}`);
  }

  return { data: (result.data as T) ?? null, error: result.error ?? null };
};

const handleError = (error: unknown): ApiResponse<never> => {
  // Aborted fetches (component unmounted, user closed modal, etc.) are not
  // real errors — return a neutral response so callers don't surface them.
  if (error instanceof Error && error.name === 'AbortError') {
    return { data: null, error: null };
  }
  const message = error instanceof Error ? error.message : 'Unknown error';
  return { data: null, error: message };
};

export const fetchJson = async <T = unknown>(
  url: string,
  options?: RequestInit
): Promise<ApiResponse<T>> => {
  try {
    const response = await fetch(url, options);
    return await handleResponse<T>(response);
  } catch (error) {
    return handleError(error);
  }
};

export const postJson = async <T = unknown>(
  url: string,
  data: unknown = {},
  headers: Record<string, string> = {},
  signal?: AbortSignal
): Promise<ApiResponse<T>> => {
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify(data),
      signal,
    });
    return await handleResponse<T>(response);
  } catch (error) {
    return handleError(error);
  }
};

export const patchJson = async <T = unknown>(
  url: string,
  data: unknown = {},
  headers: Record<string, string> = {},
  signal?: AbortSignal
): Promise<ApiResponse<T>> => {
  try {
    const response = await fetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify(data),
      signal,
    });
    return await handleResponse<T>(response);
  } catch (error) {
    return handleError(error);
  }
};
